import { PaginatedResponse } from '@/interfaces/response';
import { CashPaymentForLoan, Loan, LoanDefault, LoanInstallment, LoanLiquidation, LoanType } from '@/interfaces/loans';
import { Note } from '@/interfaces/note';
import { AxiosPromise } from 'axios';
import request from '@/umi-request';

// Prestamos

export async function getLoans(params: any = {}) {
  return request({
    url: `loans/`,
    method: 'GET',
    params
  }) as AxiosPromise<PaginatedResponse<Loan>>;
}

export async function getMineLoans(params: any = {}) {
  return request({
    url: `users/me/loans/`,
    method: 'GET',
    params
  }) as AxiosPromise<PaginatedResponse<Loan>>;
}

export async function getLoan(id: number) {
  return request({
    url: `loans/${id}/`,
    method: 'GET',
  }) as AxiosPromise<PaginatedResponse<Loan>>;
}

export function createLoan(data: any) {
  return request({
    url: 'loans/',
    method: 'POST',
    data
  }) as AxiosPromise<Loan>;
}

export function getLoanChanges(id: number | string, params: any) {
  return request({
    url: `loans/${id}/changes/`,
    method: 'GET',
    params
  }) as AxiosPromise<Loan[]>;
}

export async function updateLoanType(id: number, data: any) {
  return request({
    url: `loans/${id}/change-loan-type/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Loan>;
}

export async function verifyLoan(id: number, data?: any) {
  return request({
    url: `loans/${id}/verify/`,
    method: 'PUT',
    data
  }) as AxiosPromise<Loan>;
}

export async function cancelLoan(id: number, data?: any) {
  return request({
    url: `loans/${id}/cancel/`,
    method: 'PUT',
    data
  }) as AxiosPromise<Loan>;
}

export async function rejectLoan(id: number, data?: any) {
  return request({
    url: `loans/${id}/void/`,
    method: 'PUT',
    data
  }) as AxiosPromise<Loan>;
}

export async function updateLoanGuarantor(id: number, data: any) {
  return request({
    url: `loans/${id}/change-guarantor-data/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Loan>;
}

export async function updateLoanPaymentData(id: number, data: any) {
  return request({
    url: `loans/${id}/change-payment-data/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Loan>;
}

export async function updateGeneralData(id: number, data: any) {
  return request({
    url: `loans/${id}/change-general-data/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Loan>;
}

export async function getLoanInstallments(id: number, params: any) {
  return request({
    url: `loans/${id}/installments/`,
    method: 'GET',
    params
  }) as AxiosPromise<Loan>;
}

export async function getLoanTypes() {
  return request({
    url: `loan-types/`,
    method: 'GET'
  }) as AxiosPromise<LoanType>;
}

export async function getLoanDefaults() {
  return request({
    url: `loan-liquidations/defaults/`,
    method: 'GET'
  }) as AxiosPromise<LoanDefault>;
}

export async function sendLoanNote(id: number | string, data: { content: string }) {
  return request({
    url: `files/${id}/notes/`,
    method: 'POST',
    data
  }) as AxiosPromise<Note<Loan>>;
}

export async function getLoanNotes(id: number | string) {
  return request({
    url: `loans/${id}/notes/`,
    method: 'GET',
  }) as AxiosPromise<Note<Loan>[]>;
}

export async function getFileChanges(id: number | string, params: any) {
  return request({
    url: `loans/${id}/changes/`,
    method: 'GET',
    params
  }) as AxiosPromise<Loan[]>;
}

export async function generateCancelInstallment(id: number, data: any) {
  return request({
    url: `loans/${id}/cancellation-installment/`,
    method: 'POST',
    data
  }) as AxiosPromise<LoanInstallment>;
}

export async function getLoanLiquidations(params: any) {
  return request({
    url: `loan-liquidations/`,
    method: 'GET',
    params
  }) as AxiosPromise<PaginatedResponse<LoanLiquidation>>;
}

export async function getLoanLiquidation(id: number, params: any) {
  return request({
    url: `loan-liquidations/${id}/`,
    method: 'GET',
    params
  }) as AxiosPromise<LoanLiquidation>;
}

export async function receiveLoanLiquidation(id: number, params: any) {
  return request({
    url: `loan-liquidations/${id}/receive/`,
    method: 'PUT',
    params
  }) as AxiosPromise<LoanLiquidation>;
}

export async function cancelLoanLiquidation(id: number, params: any) {
  return request({
    url: `loan-liquidations/${id}/cancel/`,
    method: 'PUT',
    params
  }) as AxiosPromise<LoanLiquidation>;
}

export async function verifyLoanLiquidation(id: number, params: any) {
  return request({
    url: `loan-liquidations/${id}/sent/`,
    method: 'PUT',
    params
  }) as AxiosPromise<LoanLiquidation>;
}

export async function generateLoanLiquidation(data: any) {
  return request({
    url: `loan-liquidations/`,
    method: 'POST',
    data
  }) as AxiosPromise<LoanLiquidation>;
}

export async function getLoansInstallments(params: any) {
  return request({
    url: `loan-installments/`,
    method: 'GET',
    params
  }) as AxiosPromise<PaginatedResponse<LoanInstallment>>;
}

export async function markPaymentForCash(id: number) {
  return request({
    url: `loan-installments/${id}/make-cash-payment/`,
    method: 'PUT',
  }) as AxiosPromise<LoanInstallment>;
}

export async function getLoanInstallmentChanges(id: number) {
  return request({
    url: `loan-installments/${id}/changes-history/`,
    method: 'GET',
  }) as AxiosPromise<LoanInstallment[]>;
}

export async function markLoanInstallmentsVoid(id: number, data: any) {
  return request({
    url: `loan-installments/${id}/void/`,
    method: 'PUT',
    data
  }) as AxiosPromise<PaginatedResponse<LoanInstallment>>;
}

export async function recreateVoidLoanInstallment(id: number, data: any) {  
  return request({
    url: `loan-installments/${id}/recreate-void/`,
    method: 'PUT',
    data
  }) as AxiosPromise<PaginatedResponse<LoanInstallment>>;
}

export async function payLoanInstallment(id: number) {
  return request({
    url: `loan-installments/${id}/pay/`,
    method: 'PUT',
  }) as AxiosPromise<PaginatedResponse<LoanInstallment>>;
}

export async function rollbackLoanInstallment(id: number) {
  return request({
    url: `loan-installments/${id}/rollback/`,
    method: 'PUT'
  }) as AxiosPromise<PaginatedResponse<LoanInstallment>>;
}

export async function getLoanLiquidationFile(id: number) {
  return request({
    url: `loan-liquidations/${id}/export-file/`,
    method: 'GET'
  }) as AxiosPromise<string>;
}

export async function getLoanLiquidationChanges(id: number) {
  return request({
    url: `loan-liquidations/${id}/changes-history/`,
    method: 'GET',
  }) as AxiosPromise<LoanLiquidation[]>;
}

export async function registerCashPaymentForLoanInstallment(account: number, data: any) {
  return request({
    url: `accounts/${account}/collections/`,
    method: 'POST',
    data
  }) as AxiosPromise<CashPaymentForLoan>;
}

export async function getLoanPDF(id: number) {
  return request({
    url: `loans/${id}/report/`,
    method: 'GET',
    responseType: 'blob'
  }) as AxiosPromise<Blob>;
}

export async function getLoansPDF() {
  return request({
    url: `loans/monthly-report/`,
    method: 'GET',
    responseType: 'blob' 
  }) as AxiosPromise<PaginatedResponse<Loan>>;
}
